import {EventEmitter, EventSubscriber, HasEventName} from "@/modules/base/events";
import {inject, injectable} from "inversify";
import {DivisionPromotion, LeagueCompetition, LeagueDivision} from "@/modules/competitions/entity";
import {AfterSeasonEvaluationEvent} from "@/modules/season/events/AfterSeasonEvaluationEvent";
import LeagueDivisionMatchGenerator from "@/modules/competitions/services/divisions/LeagueDivisionMatchGenerator";
import DivisionTableStatisticsGenerator
  from "@/modules/competitions/services/divisions/DivisionTableStatisticsGenerator";
import {Team} from "@/modules/team/entity";
import {In} from "typeorm";
import {BeforeLeaguePromotionEvent} from "@/modules/competitions/events/BeforeLeaguePromotionEvent";
import {TableStatistics} from "@/modules/competitions/entity/TableStatistics";
import {AfterLeaguePromotionEvent} from "@/modules/competitions/events/AfterLeaguePromotionEvent";
import {BankCategory} from "@/modules/finances/enums";
import {GateTypeEnum} from "@/modules/action-points/enums";
import GateManager from "@/modules/action-points/services/GateManager";

@injectable()
export class LeagueDivisionPromotionEventSubscriber implements EventSubscriber {
  constructor(
    @inject(EventEmitter) protected em: EventEmitter,
    @inject(LeagueDivisionMatchGenerator) protected leagueDivisionMatchGenerator: LeagueDivisionMatchGenerator,
    @inject(DivisionTableStatisticsGenerator) private divisionTableStatisticsGenerator: DivisionTableStatisticsGenerator,
    @inject(GateManager) private gateManager: GateManager,
  ) {}

  priority(): number {
    return 0;
  }

  init(): { event: HasEventName; callback: (event: any) => void }[] {
    return [
      {event: AfterSeasonEvaluationEvent, callback: this.afterSeasonEvaluationEvent},
    ];
  }

  private isPromotion(compentition: LeagueCompetition, fromId: number, toId: number): boolean {
    for (const id of compentition.divisions.map(d => d.id)) {
      if (id === fromId) {
        return false;
      }

      if (id === toId) {
        return true;
      }
    }

    throw 'Divisions are not in the given competition';
  }

  private isFirstDivision(competition: LeagueCompetition, division: LeagueDivision): boolean {
    return (competition.divisions[0].id ?? null) === division.id;
  }

  async afterSeasonEvaluationEvent(event: AfterSeasonEvaluationEvent): Promise<void> {
    console.time('LeagueDivisionPromotionEventSubscriber::afterSeasonEvaluationEvent');

    const mappedTeams: { [key: number]: number[] } = {};
    const promotedTeams: { [key: number]: number[] } = {};
    const relegatedTeams: { [key: number]: number[] } = {};

    const divisions = await LeagueDivision
      .createQueryBuilder('division')
      .innerJoinAndSelect('division.promotions', 'promotion')
      .innerJoinAndSelect('division.stats', 'stats')
      .innerJoinAndSelect('stats.team', 'team')
      .innerJoinAndSelect('promotion.target_division', 'targetDivision')
      .innerJoinAndSelect('division.competition', 'competition')
      .innerJoinAndSelect('competition.divisions', 'competitionDivision')
      .innerJoin('stats.season', 'season')
      .andWhere('promotion.endOfTheSeason = true')
      .andWhere('season.id = :id', event.lastSeason)
      .getMany();

    await divisions
      .filter(d => d.competition instanceof LeagueCompetition)
      .forEachAsync(async (d: LeagueDivision) => {
        mappedTeams[d.id] ||= [];
        promotedTeams[d.id] ||= [];
        relegatedTeams[d.id] ||= [];

        await d.stats.forEachAsync(async (s: TableStatistics) => {
          await d.promotions.forEachAsync(async (p: DivisionPromotion) => {
            if (p.targetDivisionId && p.isForRank(s.rank)) {
              // promotion to target division
              mappedTeams[p.targetDivisionId] ||= [];
              mappedTeams[p.targetDivisionId].push(s.teamId);

              if (d.id !== p.targetDivisionId) {
                if (this.isPromotion(d.competition, d.id, p.targetDivisionId)) {
                  promotedTeams[p.targetDivisionId] ||= [];
                  promotedTeams[p.targetDivisionId].push(s.teamId);
                } else {
                  relegatedTeams[p.targetDivisionId] ||= [];
                  relegatedTeams[p.targetDivisionId].push(s.teamId);
                }
              }

              await this.em.emitAsync(
                new BeforeLeaguePromotionEvent(s.team, d, p.target_division as LeagueDivision, p, s)
              );

              if (p.price > 0 && s.team.user) {
                await this.gateManager.getGate(GateTypeEnum.BALANCE).currency.add(
                  p.price,
                  {
                    category: BankCategory.REWARDS,
                    label: translator.t('reward_for_division_name', {name: d.name}),
                  }
                );
              }

              this.em.stack('league-promotion', new AfterLeaguePromotionEvent(s.team, d, p.target_division as LeagueDivision, p, s));
            }
          });
        });
      });

    await Object.keys(mappedTeams).forEachAsync(async (id: number) => {
      const division = await LeagueDivision
        .createQueryBuilder('division')
        .innerJoinAndSelect('division.competition', 'competition')
        .innerJoinAndSelect('division.stats', 'stats')
        .innerJoinAndSelect('stats.team', 'team')
        .innerJoinAndSelect('competition.divisions', 'competitionDivision')
        .where('division.id = :divisionId', {divisionId: id})
        .getOneOrFail();

      console.debug(`start processing division promotion for ${division.name}`);

      division.finished = false;
      division.teams = await Team.find({where: {id: In(mappedTeams[id])}});

      if (division.competition instanceof LeagueCompetition) {
        division.promoted = await Team.find({where: {id: In(promotedTeams[id])}});
        division.relegated = await Team.find({where: {id: In(relegatedTeams[id])}});
      }

      if (this.isFirstDivision(division.competition, division)) {
        division.competition.winner = division.stats.find(s => s.rank === 1)!.team;
        await division.competition.save();
      }

      await division.save();

      await this.divisionTableStatisticsGenerator.generate(event.nextSeason, division);
      await this.leagueDivisionMatchGenerator.generate(event.nextSeason, division);

      console.debug(`end processing division promotion for ${division.name}`);
    });

    await this.em.flushAsync('league-promotion');

    console.timeEnd('LeagueDivisionPromotionEventSubscriber::afterSeasonEvaluationEvent');
  }
}